import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminInfluencers from './pages/AdminInfluencers';
import AdminInfluencerView from './pages/AdminInfluencerView';
import AdminVendors from './pages/AdminVendors';
import AdminCatalog from './pages/AdminCatalog';
import AdminRequests from './pages/AdminRequests';
import InfluencerDashboard from './pages/InfluencerDashboard';
import InfluencerExplore from './pages/InfluencerExplore';
import InfluencerRequests from './pages/InfluencerRequests';
import InfluencerProducts from './pages/InfluencerProducts';
import Settings from './pages/Settings';
import SetPassword from './pages/SetPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';

// Loading spinner
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
        <p className="text-ink-400 text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({
  children,
  requiredRole,
}: {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'influencer';
}) {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={user.role === 'admin' ? '/admin/dashboard' : '/dashboard'} replace />;
  }

  return <>{children}</>;
}

// Redirect authenticated users
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  if (user) {
    return <Navigate to={user.role === 'admin' ? '/admin/dashboard' : '/dashboard'} replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { loading } = useAuth();

  if (loading) return <LoadingScreen />;

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/admin/login"
        element={
          <PublicRoute>
            <AdminLogin />
          </PublicRoute>
        }
      />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/invite" element={<SetPassword />} /> {/* Legacy route */}
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Admin routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/influencers"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminInfluencers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/influencers/:id/view"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminInfluencerView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/vendors"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminVendors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/catalog"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminCatalog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/requests"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminRequests />
          </ProtectedRoute>
        }
      />

      {/* Influencer routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredRole="influencer">
            <InfluencerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/explore"
        element={
          <ProtectedRoute requiredRole="influencer">
            <InfluencerExplore />
          </ProtectedRoute>
        }
      />
      <Route
        path="/requests"
        element={
          <ProtectedRoute requiredRole="influencer">
            <InfluencerRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute requiredRole="influencer">
            <InfluencerProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Default redirects */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
